import React, { useState, useEffect } from "react";
import SelectionTable from "./common/SelectionTable";
import CrudModal from "../../general/CrudModal";
import { API_ENDPOINTS } from "../../../config/apiConfig";
import axios from "axios";
import { calculateSolarCapacity } from '../../../utils/solarCalculations';

const DepotSelection = ({ depots, loading, error, selectedDepots, setSelectedDepots, nextStep, prevStep, refreshDepots, loadingCircuits }) => {
  // Add state for edit modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentEditItem, setCurrentEditItem] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);

  // Add new state for add depot modal
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addError, setAddError] = useState(null);
  const [addLoading, setAddLoading] = useState(false);

  // Add these state variables at the top of your component
  const [filteredDepots, setFilteredDepots] = useState(depots);
  const [filterCriteria, setFilterCriteria] = useState({});

  // Add useEffect to update filtered depots when original depots change
  useEffect(() => {
    setFilteredDepots(depots);
  }, [depots]);

  // Add a filter change handler
  const handleFilterChange = (filters) => {
    setFilterCriteria(filters);
    
    // If no filters, show all items
    if (Object.keys(filters).length === 0) {
      setFilteredDepots(depots);
      return;
    }
    
    // Filter items based on criteria
    const filtered = depots.filter(depot => {
      return Object.entries(filters).every(([columnKey, allowedValues]) => {
        if (!allowedValues || allowedValues.length === 0) return true;
        return allowedValues.includes(depot[columnKey]);
      });
    });
    
    setFilteredDepots(filtered);
  };

  // Handle checkbox change
  const handleCheckboxChange = (depotIdOrArray, e) => {
    // Stop propagation if event is provided
    if (e) {
      e.stopPropagation();
    }
    
    setSelectedDepots((prevSelected) => {
      // Handle array of IDs (for select all/none functionality)
      if (Array.isArray(depotIdOrArray)) {
        return depotIdOrArray;
      }
      // Handle single ID toggle
      const depotId = depotIdOrArray;
      if (prevSelected.includes(depotId)) {
        return prevSelected.filter((id) => id !== depotId);
      } else {
        return [...prevSelected, depotId];
      }
    });
  };

  // Handle "Select All" checkbox
  const handleSelectAllDepots = (e) => {
    if (e) e.stopPropagation();
    if (allDepotsSelected) {
      // Deselect all depots
      handleCheckboxChange([]);
    } else {
      // Select all depots at once
      handleCheckboxChange(depots.map(depot => depot.depot_id));
    }
  };

  const allDepotsSelected = depots.length > 0 && selectedDepots.length === depots.length;

  // Define columns for the depot table
  const depotColumns = [
    { key: 'name', label: 'Depot Name', width: '20%' },
    { key: 'address', label: 'Address', width: '25%' },
    { key: 'utility', label: 'Electric Utility', width: '15%' },
    { key: 'circuit', label: 'Nearby Power Circuits (Distance)', width: '30%' }
  ];

  // Define how to render each row
  const renderDepotRow = (depot) => (
    <>
      <td style={{ width: depotColumns[0].width }}>{depot.name || "—"}</td>
      <td style={{ width: depotColumns[1].width }}>{depot.address || `${depot.lat}, ${depot.lon}`}</td>
      <td style={{ width: depotColumns[2].width }}>{depot.utility || "To be Determined"} </td>
      <td style={{ width: depotColumns[3].width }}>
        {depot.circuits && depot.circuits.length > 0 && depot.circuit_powers && depot.circuit_dists ? (
          depot.circuits.map((circuit, idx) => (
            depot.circuit_powers[idx] !== undefined && depot.circuit_dists[idx] !== undefined ? (
              <span key={idx}>
                <span style={{ fontWeight: 300, color: '#6366f1' }}>Circuit -</span> {circuit}: {(depot.circuit_powers[idx]/1000).toFixed(2)} MW ({depot.circuit_dists[idx].toFixed(1)} miles)
                {idx < depot.circuits.length - 1 ? <br/> : null}
              </span>
            ) : null
          ))
        ) : (
          <span>No circuit information available</span>
        )}
      </td>
    </>
  );

  // Define editable fields for depots - format adapted for CrudModal
  const editableFields = [
    // Basic Info
    { id: 'name', name: 'Depot Name', type: 'text', category: 'basic', required: true},
    { id: 'address', name: 'Address', type: 'address', category: 'basic', required: true},
    { id: 'construction_cost', name: 'Depot Construction Cost', type: 'currency', category: 'basic', tooltip: 'Fixed cost for site preparation.' },
    { id: 'depot_lifespan', name: 'Depot Lifespan (years)', type: 'number', category: 'basic', tooltip: 'Expected operational life of the depot in years.'},
    
    // Grid Info
    { id: 'utility', name: 'Electric Utility', type: 'text', category: 'grid', tooltip: 'Name of the local electric utility company serving this location.', disabled: true, defaultValue: 'To be determined automatically in a future update' },
    { id: 'circuit_info', name: 'Circuit Information', specialType: 'circuit_info', category: 'grid', tooltip: 'Information about power circuits available near this location.' },
    { id: 'interconnection_cost', name: 'Interconnection Cost (per mile)', type: 'currency', category: 'grid', tooltip: 'Cost to connect to the grid per mile of distance.' },
    { id: 'interconnection_lifespan', name: 'Interconnection Lifespan (years)', type: 'number', category: 'grid', tooltip: 'Expected useful life of the grid interconnection in years.' },
    
    // Charger Installation
    { id: 'charger_types', name: 'Charger Types', specialType: 'charger_types', category: 'charger', tooltip: 'Specifications for available charger types, including power limits and costs.' },
    { id: 'charger_lifespan', name: 'Charger Lifespan (years)', type: 'number', category: 'charger', tooltip: 'Expected useful life of chargers in years.' },
    
    // Solar parameters
    { id: 'consider_solar', name: 'Consider Solar Installation', type: 'switch', category: 'solar', tooltip: 'Enable to include solar panel installation in the optimization model.'},
    // Solar capacity parameters
    { id: 'solar_area', name: 'Available Solar Installation Area (sq ft)', type: 'number', category: 'solar', tooltip: 'Total area available for solar panel installation in square feet. Needed to estimate maximum solar capacity.', defaultValue: 2000 },
    { id: 'solar_type', name: 'Solar Installation Type', type: 'select', category: 'solar', tooltip: 'Type of solar installation, which affects the utilization factor of the available area.',
      options: [
        { value: 'flat', label: 'Flat-roof systems (Utilization factor=0.75)' },
        { value: 'tilted', label: 'Tilted-roof systems (Utilization factor=0.85)' },
        { value: 'carport', label: 'Solar carport systems (Utilization factor=0.75)' },
        { value: 'ground', label: 'Ground-mount systems (Utilization factor=0.90)' }
      ],
      defaultValue: 'flat'
    },
    { id: 'solar_max_capacity', name: 'Maximum Solar Capacity (kW)', type: 'number', category: 'solar', tooltip: 'Maximum solar capacity in kW. This value is automatically estimated when you change the area or installation type. You can also manually adjust it if needed.', defaultValue: 30.0},
    { id: 'solar_generation_profile', name: 'Solar Generation Profile', type: 'json', category: 'solar', tooltip: 'Hourly profile showing solar power generation as a ratio of maximum capacity.' },
    // Solar cost parameters
    { id: 'solar_fixed_cost', name: 'Solar Fixed Installation Cost', type: 'currency', category: 'solar', tooltip: 'One-time fixed cost for installing solar power, regardless of capacity.' },
    { id: 'solar_capacity_cost', name: 'Solar Capacity Cost (per kW)', type: 'currency', category: 'solar', tooltip: 'Cost per kilowatt of installed solar capacity.' },
    { id: 'solar_lifespan', name: 'Solar Lifespan (years)', type: 'number', category: 'solar', tooltip: 'Expected useful life of the solar installation in years.' },
    
    // Battery parameters
    { id: 'consider_battery', name: 'Consider Battery Installation', type: 'switch', category: 'battery', tooltip: 'Enable to include battery storage in the optimization model.'},
    // Battery capacity parameters
    { id: 'battery_max_capacity', name: 'Maximum Battery Capacity (kWh)', type: 'number', category: 'battery', tooltip: 'Maximum energy storage capacity in kilowatt-hours.' },
    { id: 'battery_max_power', name: 'Maximum Battery Power (kW)', type: 'number', category: 'battery', tooltip: 'Maximum power output/input rate of the battery in kilowatts.' },
    // Battery efficiency parameters
    { id: 'battery_charge_efficiency', name: 'Battery Charging Efficiency', type: 'number', category: 'battery', tooltip: 'Efficiency of storing energy in the battery (0-1).' },
    { id: 'battery_discharge_efficiency', name: 'Battery Discharging Efficiency', type: 'number', category: 'battery', tooltip: 'Efficiency of retrieving energy from the battery (0-1).' },
    { id: 'battery_min_soc', name: 'Minimum Battery State of Charge', type: 'number', category: 'battery', tooltip: 'Minimum allowed battery charge level as a fraction (0-1).' },
    { id: 'battery_max_soc', name: 'Maximum Battery State of Charge', type: 'number', category: 'battery', tooltip: 'Maximum allowed battery charge level as a fraction (0-1).' },
    // Battery cost parameters
    { id: 'battery_fixed_cost', name: 'Battery Fixed Installation Cost', type: 'currency', category: 'battery', tooltip: 'One-time fixed cost for installing battery storage, regardless of capacity.' },
    { id: 'battery_capacity_cost', name: 'Battery Capacity Cost (per kWh)', type: 'currency', category: 'battery', tooltip: 'Cost per kilowatt-hour of installed battery capacity.' },
    { id: 'battery_lifespan', name: 'Battery Lifespan (years)', type: 'number', category: 'battery', tooltip: 'Expected useful life of the battery in years.' }
  ];

  // Handle edit button click
  const handleEditDepot = (depot) => {
    setCurrentEditItem(depot);
    setIsEditModalOpen(true);
    setEditError(null);
  };

  // Handle save changes - Update to include auto-calculation
  const handleSaveChanges = async (updatedDepot) => {
    setEditLoading(true);
    setEditError(null);
    
    try {
      // Make sure we have the depot_id
      if (!updatedDepot.depot_id) {
        throw new Error("Depot ID is missing");
      }
      
      // Auto-calculate solar capacity if not manually set
      if (updatedDepot.consider_solar && 
          updatedDepot.solar_area && 
          updatedDepot.solar_type) {
        // Only auto-calculate if the user hasn't manually set a value or if the area/type has changed
        if (!updatedDepot.solar_max_capacity || 
            updatedDepot.solar_area !== currentEditItem.solar_area ||
            updatedDepot.solar_type !== currentEditItem.solar_type) {
          updatedDepot.solar_max_capacity = calculateSolarCapacity(
            updatedDepot.solar_area, 
            updatedDepot.solar_type
          );
        }
      }
      
      // Process circuit data - ensure arrays are properly formatted
      if (updatedDepot.circuits && Array.isArray(updatedDepot.circuits)) {
        // Make sure circuit_powers and circuit_dists match the length of circuits
        if (!updatedDepot.circuit_powers || updatedDepot.circuit_powers.length !== updatedDepot.circuits.length) {
          updatedDepot.circuit_powers = updatedDepot.circuits.map((_, i) => 
            (updatedDepot.circuit_powers && updatedDepot.circuit_powers[i]) || 0
          );
        }
        
        if (!updatedDepot.circuit_dists || updatedDepot.circuit_dists.length !== updatedDepot.circuits.length) {
          updatedDepot.circuit_dists = updatedDepot.circuits.map((_, i) => 
            (updatedDepot.circuit_dists && updatedDepot.circuit_dists[i]) || 0
          );
        }
      }
      
      // Process manual circuit data
      if (updatedDepot.manual_capacity > 0 || updatedDepot.manual_distance > 0) {
        // If circuits array is empty, initialize it with the manual circuit
        if (!updatedDepot.circuits || updatedDepot.circuits.length === 0) {
          updatedDepot.circuits = ["Manual Circuit"];
          updatedDepot.circuit_powers = [parseFloat(updatedDepot.manual_capacity) * 1000]; // Convert MW to kW
          updatedDepot.circuit_dists = [parseFloat(updatedDepot.manual_distance)];
        }
      }
      
      // Get the auth token from localStorage
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error("Authentication token is missing");
      }
      
      // Make the API call to update the depot
      const response = await axios.put(
        API_ENDPOINTS.editDepot,
        updatedDepot,
        {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Check if the request was successful
      if (response.status === 200) {
        console.log("Depot updated successfully:", response.data);
        
        // Refresh the depots list
        if (typeof refreshDepots === 'function') {
          refreshDepots();
        }
        
        // Close the modal
        setIsEditModalOpen(false);
        setCurrentEditItem(null);
      } else {
        throw new Error("Failed to update depot");
      }
    } catch (error) {
      console.error("Error updating depot:", error);
      setEditError(error.response?.data?.error || error.message || "An error occurred while updating the depot");
    } finally {
      setEditLoading(false);
    }
  };

  // Add depot fields definition
  const depot_fields = [
    { id: "depot_name", name: "Depot Name", type: "string", required: true },
    { id: "address", name: "Address", type: "address", required: true },
  ];

  // Add info message about additional configuration options
  const addDepotModalInfo = (
    <div className="depot-add-info" style={{
      marginTop: '50px',
      marginLeft: '20px',
      marginRight: '20px',
      padding: '10px 15px',
      backgroundColor: '#f0f4ff',
      borderRadius: '5px',
      borderLeft: '3px solid #5E5ADE',
      fontSize: '1.7rem',
      lineHeight: '1.4'
    }}>
      <p><strong>Note:</strong> After adding a depot, you can configure additional details by clicking the edit button, including:</p>
      <ul style={{ paddingLeft: '10px', marginTop: '5px' }}>
        <li>• Grid connection information</li>
        <li>• Charger specifications</li>
        <li>• Solar installation options</li>
        <li>• Battery storage options</li>
      </ul>
    </div>
  );

  // Add depot handler
  const handleAddDepot = async (formData) => {
    setAddLoading(true);
    setAddError(null);
    
    try {
      const depotData = {
        name: formData.depot_name,
        address: formData.address,
      };

      // Get auth token
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error("Authentication token is missing");
      }

      const response = await axios.post(
        API_ENDPOINTS.addDepot,
        depotData,
        {
          headers: {
            Authorization: `Token ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      // If successful, refresh the depots list
      if (response.status === 200 || response.status === 201) {
        if (typeof refreshDepots === 'function') {
          refreshDepots();
        }
        
        // Close the modal
        setIsAddModalOpen(false);
      }
    } catch (error) {
      console.error("Error adding depot:", error);
      setAddError(error.response?.data?.error || error.message || "An error occurred while adding the depot");
    } finally {
      setAddLoading(false);
    }
  };

  // Delete depots handler
  const handleDeleteSelectedDepots = async () => {
    if (selectedDepots.length === 0) {
      return;
    }
    
    try {
      // Get auth token
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error("Authentication token is missing");
      }
      
      // Send a POST request with the depot_ids in the request body
      const response = await axios.post(
        API_ENDPOINTS.deleteDepot,
        { depot_ids: selectedDepots },
        {
          headers: {
            Authorization: `Token ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
       
      if (response.status >= 200 && response.status < 300) {
        // Refresh the depots list using the provided function
        if (typeof refreshDepots === 'function') {
          refreshDepots();
        }
        
        // Clear the selected depots (this should now be handled by the parent)
        setSelectedDepots([]);
      } else {
        throw new Error("Unexpected response status: " + response.status);
      }
    } catch (error) {
      console.error("Error deleting depots:", error);
    }
  };

  if (loading) {
    return <div className="step-container"><p>Loading depots...</p></div>;
  }

  if (error) {
    return <div className="step-container"><p className="error">{error}</p></div>;
  }

  return (
    <div className="step-container">
      {/* Add the depot action buttons */}
      <div className="entity-actions depot-actions">
        <CrudModal 
          entity="Depot"
          mode="add"
          fields={depot_fields}
          onSubmit={handleAddDepot}
          buttonText="Add Depot"
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          isLoading={addLoading}
          error={addError}
          additionalContent={addDepotModalInfo}
        />
        
        {selectedDepots.length > 0 && (
          <CrudModal 
            entity="Depot"
            mode="delete"
            onSubmit={handleDeleteSelectedDepots}
            selectedItems={depots.filter(depot => selectedDepots.includes(depot.depot_id))}
            buttonPosition="right"
          />
        )}
        {loadingCircuits && (
          <div className="loading-indicator">
            <span>Loading circuit data...</span>
          </div>
        )}
      </div>

      {depots.length === 0 ? (
        <p>No depots found. Please add depots to continue.</p>
      ) : (
        <>
          <div className="selection-summary">
            <p>
              Showing {depots.length} depots 
              ({selectedDepots.length} selected)
            </p>
          </div>
          <SelectionTable
            items={depots}
            selectedItems={selectedDepots}
            handleCheckboxChange={handleCheckboxChange}
            handleSelectAll={handleSelectAllDepots}
            allSelected={allDepotsSelected}
            columns={depotColumns}
            itemIdKey="depot_id"
            renderRow={renderDepotRow}
            onEditItem={handleEditDepot}
            filteredItems={filteredDepots}
            onFilterChange={handleFilterChange}
          />
        </>
      )}

      {/* Use a modal overlay similar to what's in inputs.js */}
      {isEditModalOpen && currentEditItem && (
        <div className="modal-overlay">
          <CrudModal
            entity="Depot"
            mode="edit"
            fields={editableFields}
            onSubmit={handleSaveChanges}
            selectedItems={[currentEditItem]}
            initialData={currentEditItem}
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            isLoading={editLoading}
            error={editError}
          />
        </div>
      )}
    </div>
  );
};

export default DepotSelection; 